import { useState } from "react";
import { Link, useSearchParams } from "@remix-run/react";
import { useQuery, useAction } from "convex/react";
import { api } from "../../../convex/_generated/api";
import {
  MagnifyingGlassIcon,
  SparklesIcon,
  BookOpenIcon,
  UserGroupIcon,
  CalendarIcon,
  CreditCardIcon,
  Cog6ToothIcon,
  CheckCircleIcon,
  XCircleIcon,
  QuestionMarkCircleIcon,
  ArrowRightIcon,
} from "@heroicons/react/24/outline";

const CATEGORY_METADATA: Record<string, { icon: any; description: string; color: string }> = {
  "Getting Started": {
    icon: BookOpenIcon,
    description: "New to MinHub? Learn the basics of setting up your ministry.",
    color: "bg-coral/10 text-primary border-primary/20",
  },
  "Events & Attendance": {
    icon: CalendarIcon,
    description: "Manage service times, recurring templates, kiosk check-in, and checkouts.",
    color: "bg-secondary/20 text-darkGrey border-secondary/40",
  },
  "People & Groups": {
    icon: UserGroupIcon,
    description: "Organize ministry members, families, smart groups, and portal chats.",
    color: "bg-indigo-50 text-indigo-600 border-indigo-200",
  },
  "Giving & Billing": {
    icon: CreditCardIcon,
    description: "Set up Stripe Connect giving, transactions, and subscription plans.",
    color: "bg-emerald-50 text-emerald-600 border-emerald-200",
  },
  Settings: {
    icon: Cog6ToothIcon,
    description: "Customize grade labels, kiosk settings, and ministry permissions.",
    color: "bg-amber-50 text-amber-600 border-amber-200",
  },
};

export default function KnowledgeBaseIndex() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearch = searchParams.get("q") || "";
  const [searchQuery, setSearchQuery] = useState(initialSearch);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  // AI Q&A State
  const [aiQuestion, setAiQuestion] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState<{
    answer: string;
    citations: Array<{ id: string; title: string; slug: string; category?: string; excerpt?: string }>;
    isPaying: boolean;
  } | null>(null);
  const [feedbackGiven, setFeedbackGiven] = useState<boolean | null>(null);

  const askAiAction = useAction(api.aiQa.askKnowledgeBase);

  // Queries
  const categories = useQuery(api.supportArticles.getCategories) || [];
  const publishedArticles = useQuery(api.supportArticles.getPublishedArticles, {
    category: activeCategory || undefined,
    search: searchQuery || undefined,
  }) || [];

  const featuredArticles = publishedArticles.filter((art) => art.isFeatured);
  const displayArticles = publishedArticles.slice(0, 12);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setSearchParams({ q: searchQuery.trim() });
    } else {
      setSearchParams({});
    }
  };

  const handleAskAi = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!aiQuestion.trim() || aiLoading) return;

    setAiLoading(true);
    setFeedbackGiven(null);
    try {
      const res = await askAiAction({ question: aiQuestion.trim() });
      setAiResult(res);
    } catch (err) {
      console.error("Failed to query AI Q&A:", err);
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div className="w-full h-full min-h-screen overflow-y-auto bg-slate-50 font-sans text-darkGrey">
      {/* Kit.com-style Hero Header with MinHub Brand Identity */}
      <div className="bg-gradient-to-b from-darkGrey to-grey text-white pt-16 pb-20 px-4 sm:px-6 lg:px-8 text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#88d8de_1px,transparent_1px)] [background-size:16px_16px]" />
        
        <div className="max-w-4xl mx-auto relative z-10">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-secondary/20 text-secondary border border-secondary/30 mb-4">
            <SparklesIcon className="w-4 h-4 text-secondary" />
            MinHub Help Center & Knowledge Base
          </span>

          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight mb-4 text-white font-sans">
            How can we help your ministry today?
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-8">
            Search our comprehensive guides, tutorial articles, or ask our AI assistant for instant answers grounded in MinHub documentation.
          </p>

          {/* Search Bar */}
          <form onSubmit={handleSearchSubmit} className="max-w-2xl mx-auto relative shadow-2xl rounded-2xl">
            <div className="relative flex items-center">
              <MagnifyingGlassIcon className="w-5 h-5 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none z-10" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search articles, topics, keywords..."
                className="w-full pl-12 pr-28 py-4 bg-white text-darkGrey placeholder-slate-400 text-base rounded-2xl focus:outline-none focus:ring-4 focus:ring-primary/40 border border-slate-200"
              />
              <button
                type="submit"
                className="absolute right-2 top-1/2 -translate-y-1/2 px-5 py-2.5 bg-primary hover:bg-primary/90 text-white font-semibold rounded-xl text-sm transition-all duration-200"
              >
                Search
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Main Container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-20 pb-20">
        {/* Category Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-12">
          {Object.entries(CATEGORY_METADATA).map(([catName, meta]) => {
            const Icon = meta.icon;
            const isSelected = activeCategory === catName;
            return (
              <button
                key={catName}
                onClick={() => setActiveCategory(isSelected ? null : catName)}
                className={`flex flex-col text-left p-5 rounded-2xl bg-white transition-all duration-200 shadow-sm hover:shadow-md ${
                  isSelected
                    ? "border-2 border-primary ring-4 ring-primary/20 shadow-md scale-[1.02]"
                    : "border border-slate-200 hover:border-slate-300"
                }`}
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center border mb-3 ${meta.color}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="font-bold text-darkGrey text-base mb-1">{catName}</h3>
                <p className="text-xs text-slate-500 line-clamp-2">{meta.description}</p>
              </button>
            );
          })}
        </div>

        {/* AI Q&A Section for Paying Users */}
        <div className="mb-14 bg-gradient-to-r from-slate-900 via-darkGrey to-slate-900 text-white rounded-3xl p-6 sm:p-8 shadow-xl border border-slate-800">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl bg-secondary/20 flex items-center justify-center text-secondary border border-secondary/30">
                <SparklesIcon className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Ask MinHub AI Assistant</h2>
                <p className="text-xs text-slate-400">Get grounded answers with direct citations to Knowledge Base articles.</p>
              </div>
            </div>
            <span className="px-3 py-1 bg-secondary/20 text-secondary border border-secondary/30 text-xs font-semibold rounded-full">
              Paying Member Feature
            </span>
          </div>

          <form onSubmit={handleAskAi} className="mb-6">
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                value={aiQuestion}
                onChange={(e) => setAiQuestion(e.target.value)}
                placeholder="e.g. How do I enable family kiosk check-in and print claim tags?"
                className="flex-1 bg-slate-800/90 text-white placeholder-slate-400 px-4 py-3.5 rounded-xl border border-slate-700 focus:outline-none focus:ring-2 focus:ring-secondary text-sm"
              />
              <button
                type="submit"
                disabled={!aiQuestion.trim() || aiLoading}
                className="bg-secondary text-darkGrey hover:bg-secondary/90 font-bold px-6 py-3.5 rounded-xl text-sm disabled:opacity-50 transition-colors flex items-center justify-center gap-2 shrink-0"
              >
                {aiLoading ? (
                  <span>Thinking...</span>
                ) : (
                  <>
                    <span>Ask AI</span>
                    <ArrowRightIcon className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </form>

          {/* AI Q&A Answer Display */}
          {aiResult && (
            <div className="bg-slate-800/80 rounded-2xl p-6 border border-slate-700 space-y-4 animate-slide-top">
              <div className="prose prose-invert max-w-none text-sm leading-relaxed text-slate-200">
                <p className="whitespace-pre-wrap">{aiResult.answer}</p>
              </div>

              {/* Citations List */}
              {aiResult.citations.length > 0 && (
                <div className="pt-4 border-t border-slate-700">
                  <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                    Referenced Articles ({aiResult.citations.length})
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {aiResult.citations.map((citation) => (
                      <Link
                        key={citation.id}
                        to={`/help/article/${citation.slug}`}
                        className="p-2.5 rounded-lg bg-slate-900/60 hover:bg-slate-900 border border-slate-700/80 hover:border-secondary/50 text-xs flex items-center justify-between text-slate-200 transition-colors"
                      >
                        <span className="font-medium truncate mr-2">{citation.title}</span>
                        <span className="text-secondary shrink-0 font-mono text-[10px]">Read &rarr;</span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* "Did that answer your question?" Gate */}
              <div className="pt-4 border-t border-slate-700/80 flex items-center justify-between flex-wrap gap-3">
                <span className="text-xs text-slate-300 font-medium">Did that answer your question?</span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setFeedbackGiven(true)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors ${
                      feedbackGiven === true
                        ? "bg-emerald-500 text-white"
                        : "bg-slate-700 text-slate-200 hover:bg-slate-600"
                    }`}
                  >
                    <CheckCircleIcon className="w-4 h-4" />
                    Yes, helpful!
                  </button>
                  <button
                    onClick={() => setFeedbackGiven(false)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors ${
                      feedbackGiven === false
                        ? "bg-primary text-white"
                        : "bg-slate-700 text-slate-200 hover:bg-slate-600"
                    }`}
                  >
                    <XCircleIcon className="w-4 h-4" />
                    No, contact support
                  </button>
                </div>
              </div>

              {/* Escalation Prompt when user answers "No" */}
              {feedbackGiven === false && (
                <div className="p-4 bg-primary/10 border border-primary/30 rounded-xl text-xs text-slate-200 space-y-2">
                  <p className="font-semibold text-primary">Need human assistance?</p>
                  <p>
                    We're sorry our AI assistant didn't fully answer your request. Click below to open a support ticket with our human team.
                  </p>
                  <a
                    href="#support-ticket"
                    onClick={(e) => {
                      e.preventDefault();
                      // Trigger support chat popup or ticket flow
                      const chatBtn = document.querySelector('button[aria-label="Open support chat"]') as HTMLButtonElement;
                      if (chatBtn) chatBtn.click();
                    }}
                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary text-white font-bold rounded-lg text-xs hover:bg-primary/90 transition-colors"
                  >
                    <QuestionMarkCircleIcon className="w-4 h-4" />
                    Create Human Support Ticket
                  </a>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Featured Articles Section */}
        {featuredArticles.length > 0 && !searchQuery && !activeCategory && (
          <div className="mb-12">
            <h2 className="text-xl font-bold text-darkGrey mb-4 flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-primary" />
              Featured Help Articles
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {featuredArticles.map((art) => (
                <Link
                  key={art._id}
                  to={`/help/article/${art.slug}`}
                  className="p-6 rounded-2xl bg-white border border-slate-200 hover:border-primary/40 hover:shadow-lg transition-all duration-200 group flex flex-col justify-between"
                >
                  <div>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-primary bg-primary/10 px-2.5 py-1 rounded-md">
                      {art.category || "Guide"}
                    </span>
                    <h3 className="font-bold text-darkGrey text-lg mt-3 group-hover:text-primary transition-colors">
                      {art.title}
                    </h3>
                    <p className="text-slate-500 text-xs mt-2 line-clamp-3">
                      {art.excerpt || art.content.slice(0, 140)}...
                    </p>
                  </div>
                  <div className="mt-4 text-xs font-semibold text-primary flex items-center gap-1">
                    Read guide &rarr;
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Articles List Header & Grid */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-darkGrey">
              {searchQuery
                ? `Search results for "${searchQuery}"`
                : activeCategory
                ? `${activeCategory} Articles`
                : "All Knowledge Articles"}
            </h2>
            <span className="text-xs text-slate-500">
              {publishedArticles.length} article{publishedArticles.length !== 1 ? "s" : ""}
            </span>
          </div>

          {publishedArticles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {displayArticles.map((art) => (
                <Link
                  key={art._id}
                  to={`/help/article/${art.slug}`}
                  className="p-5 rounded-2xl bg-white border border-slate-200 hover:border-slate-300 hover:shadow-md transition-all duration-200 flex flex-col justify-between"
                >
                  <div>
                    <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                      {art.category || "General"}
                    </span>
                    <h3 className="font-semibold text-darkGrey text-base mt-2 hover:text-primary transition-colors">
                      {art.title}
                    </h3>
                    <p className="text-slate-500 text-xs mt-1.5 line-clamp-2">
                      {art.excerpt || art.content.slice(0, 100)}...
                    </p>
                  </div>
                  <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-400">
                    <span>{art.viewCount || 0} views</span>
                    <span className="text-primary font-medium">Read &rarr;</span>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="p-12 text-center bg-white rounded-2xl border border-slate-200">
              <QuestionMarkCircleIcon className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <h3 className="text-base font-bold text-darkGrey">No articles found</h3>
              <p className="text-xs text-slate-500 max-w-sm mx-auto mt-1">
                We couldn't find any articles matching your search criteria. Try a different search term or ask our AI assistant!
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
